// The words the web app shows, in the person's language: the English catalogue (en.ts) or the Hindi one
// (hi.ts). The language is the person's, from their profile once they sign in; `useWords` gives a
// component its catalogue and `useLanguage` the language, for dates and numbers.
import { createContext, useContext, type ReactNode } from "react";
import { wordsOf } from "./catalogue.js";
import { en, type Words } from "./en.js";
import { hi } from "./hi.js";
import type { Language } from "./language.js";

export type { Words };

export const CATALOGUES: Readonly<Record<Language, Words>> = { en, hi: wordsOf(hi) };

interface Speaking {
  readonly language: Language;
  /** Switches the language at once, and keeps it on the person's profile. */
  readonly setLanguage: (language: Language) => void;
}

const LanguageContext = createContext<Speaking>({ language: "en", setLanguage: () => undefined });

export function LanguageProvider({ language, setLanguage, children }: Speaking & { readonly children: ReactNode }) {
  return <LanguageContext.Provider value={{ language, setLanguage }}>{children}</LanguageContext.Provider>;
}

/** The language shown now, and how to switch it. */
export const useLanguage = (): Speaking => useContext(LanguageContext);

/** The catalogue of the language shown now. */
export const useWords = (): Words => CATALOGUES[useContext(LanguageContext).language];

/** The other language: what the language switch offers. */
export const otherLanguage = (language: Language): Language => (language === "en" ? "hi" : "en");
